import {
    useLayoutEffect,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    View,
    Image
} from "react-native";
import type {
    TempCompanyData
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    TextAreaInput,
    PageContainer,
    TextInput,
    SelectBox,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    SectorsObjectType
} from "../../../../../services/restAPI/actions/getSectors/types";
import type {
    UpdateRequestForEditCompanyRequestType
} from "../../../../../services/restAPI/actions/updateRequestForEditCompany/types";
import type AppStackParamList from "../../../../../types/navigation/app";
import type {
    SelectedFileType
} from "../../../editProfile/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ArrowLeftRight,
    ChevronLeft,
    PencilOff,
    Pencil,
    Check
} from "lucide-react-native";
import ImageCropPicker from "react-native-image-crop-picker";
import validator from "validator";
import gmkonyalogo from "../../../../../assets/images/gmkonyalogo.png";
import {
    RESTService
} from "../../../../../services/restAPI";
import {
    handleUpload
} from "../../../../../utils";
import {
    IS_EXPORTS
} from "../constant";

const CompanyEditView = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "CompanyEditView">>();

    const {
        updateRequest
    } = route.params;
    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [companyData,
        setCompanyData] = useState<TempCompanyData>();
    const [updateParams,
        setUpdateParams]= useState<UpdateRequestForEditCompanyRequestType>({
            requestID: updateRequest._id
        });
    const [sectorsData,
        setSectorsData]= useState<Array<SectorsObjectType>>([]);
    const [successDialog,
        setSuccessDialog] = useState(false);
    const [verificationDialog,
        setVerificationDialog] = useState(false);
    const [deleteSuccessDialog,
        setDeleteSuccessDialog] = useState(false);
    const [deleteVerificationDialog,
        setDeleteVerificationDialog] = useState(false);
    const [loading,
        setLoading] = useState(true);
    const [selectedImage,
        setSelectedImage] = useState<string | null>(null);
    const [selctedFile,
        setSelectedFile] = useState<SelectedFileType | null>(null);

    useEffect(() => {
        getSectors();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("updateCompanyInformation")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const getSectors = () => {
        RESTService.action("GetSectors", null)
            .then((res) => {

                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setSectorsData(res.payload);

                    if(!(updateRequest.companyID && updateRequest.companyID.length && updateRequest.requestType === "edit")) {
                        const newSectors =  res.payload.filter((e) => updateRequest.sectorIDs?.includes(e._id));
                        const _newSectors = newSectors.map(e => {
                            return {
                                sectorID: e._id,
                                sectorName: e.value
                            };
                        });

                        setCompanyData({
                            aboutUs: updateRequest.aboutUs,
                            address: updateRequest.address,
                            companyID: "",
                            foundationYear: updateRequest.foundationYear,
                            isCompanyExports: updateRequest.isExports,
                            logoURL: updateRequest.logoURL,
                            mail: updateRequest.mail,
                            phoneNumber: updateRequest.phoneNumber,
                            sectors: _newSectors,
                            taxNumber: updateRequest.taxNumber,
                            taxOffice: updateRequest.taxOffice,
                            title: updateRequest.title,
                            website: updateRequest.website
                        });
                        setLoading(false);
                    } else {
                        getCompanyData(res.payload);
                    }
                }

            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const getCompanyData = (_sectors: Array<SectorsObjectType>) => {
        if(updateRequest.companyID && updateRequest.companyID.length && updateRequest.requestType === "edit") {
            RESTService.action("GetMyCompanyDetail", {
                companyID: updateRequest.companyID
            })
                .then((res) => {
                    if(res.code === 200 || res.code === 201 || res.code === 202) {
                        let updatedSectors;

                        if(updateRequest.sectorIDs && updateRequest.sectorIDs.length) {
                            const _updatedSectors = _sectors.filter(e => updateRequest.sectorIDs?.includes(e._id));

                            updatedSectors = _updatedSectors.map(e => {
                                return {
                                    sectorID: e._id,
                                    sectorName: e.value
                                };
                            });
                        }

                        setCompanyData({
                            aboutUs: updateRequest.aboutUs ? updateRequest.aboutUs : res.payload.aboutUs,
                            address: updateRequest.address ? updateRequest.address : res.payload.address,
                            companyID: updateRequest.companyID ? updateRequest.companyID : res.payload.companyID,
                            foundationYear: updateRequest.foundationYear ? updateRequest.foundationYear : res.payload.foundationYear,
                            isCompanyExports: updateRequest.isExports !== undefined ? updateRequest.isExports : res.payload.isCompanyExports,
                            logoURL: updateRequest.logoURL ? updateRequest.logoURL : res.payload.logoURL,
                            mail: updateRequest.mail ? updateRequest.mail : res.payload.mail,
                            phoneNumber: updateRequest.phoneNumber ? updateRequest.phoneNumber : res.payload.phoneNumber,
                            sectors: updatedSectors && updatedSectors.length ? updatedSectors : res.payload.sectors,
                            taxNumber: updateRequest.taxNumber ? updateRequest.taxNumber : res.payload.taxNumber,
                            taxOffice: updateRequest.taxOffice ? updateRequest.taxOffice : res.payload.taxOffice,
                            title: updateRequest.title ? updateRequest.title : res.payload.title,
                            website: updateRequest.website ? updateRequest.website : res.payload.website
                        });
                    }
                    setLoading(false);
                }).catch((err) => {
                    console.log(err);
                    setLoading(false);
                });
        }
    };

    const cancelEditRequest = () => {
        setDeleteVerificationDialog(false);
        RESTService.action("CancelRequestForUpdateCompany", {
            requestID: updateRequest._id
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setDeleteSuccessDialog(true);
                }

            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const updateEditRequest = () => {
        setVerificationDialog(false);
        RESTService.action("UpdateRequestForEditCompany", updateParams)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(selctedFile) {
                        if(updateRequest.companyID && updateRequest.companyID.length) {
                            handleUpdateCompanyLogo(updateRequest.companyID);
                        } else {
                            handleUpdateNewCompanyLogo(updateParams.requestID);
                        }
                        return;
                    }
                    setSuccessDialog(true);
                }

            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleUpdateCompanyLogo = (companyID: string) => {
        if(!selctedFile) {
            return;
        }

        RESTService.action("UpdateCompanyPhoto", {
            companyID: companyID
        })
            .then(async (res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {

                    await handleUpload(selctedFile, res.payload.uploadToken)
                        .then(() => {
                            setSuccessDialog(true);
                        })
                        .catch((err) => {
                            console.log("err", err);
                            throw err;
                        });
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const handleUpdateNewCompanyLogo = (requestID: string) => {
        if(!selctedFile) {
            return;
        }

        RESTService.action("CreateCompanyPhoto", {
            requestID: requestID
        })
            .then(async (res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {

                    await handleUpload(selctedFile, res.payload.uploadToken)
                        .then(() => {
                            setSuccessDialog(true);
                        })
                        .catch((err) => {
                            console.log("err", err);
                            throw err;
                        });
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const renderProfilePhoto = () => {
        return <View
            style={[
                stylesheet.profilePhotoWrapper,
                {
                    borderRadius: radiuses.full,
                    marginTop: spaces.spacingLg,
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <Image
                source={selectedImage || updateRequest.logoURL ? {
                    uri: selectedImage ? selectedImage : updateRequest.logoURL ? updateRequest.logoURL : ""
                } : gmkonyalogo}
                resizeMode="cover"
                style={[
                    stylesheet.profilePhotoImage,
                    {
                        borderRadius: radiuses.md
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.photoEditButton,
                    {
                        right: -spaces.spacingSm,
                        bottom: -spaces.spacingSm,
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.full,
                        padding: spaces.spacingSm,
                        borderWidth: borders.subtract,
                        borderColor: colors.content.border.subtle
                    }
                ]}
                onPress={() => {
                    ImageCropPicker.openPicker({
                        cropping: true,
                        multiple: false
                    }).then(image => {
                        const format = image.mime?.split("/")[1];

                        if (image.size > 20 * 1024 * 1024) {
                            NCoreUIKitToast.open({
                                title: localize("imageTooLarge"),
                                type: "warning"
                            });
                            return;
                        }

                        if (![
                            "jpg",
                            "jpeg",
                            "png",
                            "heic"
                        ].includes(format as string)) {
                            NCoreUIKitToast.open({
                                title: localize("invalidFormat"),
                                type: "warning"
                            });
                            return;
                        }

                        const selectedFile = {
                            name: image.path.split("/").pop(),
                            type: image.mime,
                            uri: Platform.OS === "ios" ? image.path.startsWith("file://") ? image.path : `file:/${image.path}` : image.path
                        } as unknown as SelectedFileType;
                        setSelectedImage(image.path);
                        setSelectedFile(selectedFile);
                    }).catch((err) => {
                        console.error(err);
                    });
                }}
            >
                <Pencil
                    size={20}
                />
            </TouchableOpacity>
        </View>;
    };

    const renderMyCompanyData = () => {
        if(!companyData) {
            return null;
        }
        const sectorInitialSelected: Array<{
                __title: string;
                __key: string;
            }> = companyData?.sectors?.map((e) => {
                return {
                    __key: e.sectorID,
                    __title: e.sectorName
                };
            }) || [];

        const currentData = {
            title: updateParams.title !== undefined ? updateParams.title : companyData?.title,
            foundationYear: updateParams.foundationYear !== undefined ? updateParams.foundationYear : companyData?.foundationYear,
            mail: updateParams.mail !== undefined ? updateParams.mail : companyData?.mail,
            aboutUs: updateParams.aboutUs !== undefined ? updateParams.aboutUs : companyData?.aboutUs,
            address: updateParams.address !== undefined ? updateParams.address : companyData?.address,
            phoneNumber: updateParams.phoneNumber !== undefined ? updateParams.phoneNumber : companyData?.phoneNumber,
            taxOffice: updateParams.taxOffice !== undefined ? updateParams.taxOffice : companyData?.taxOffice,
            taxNumber: updateParams.taxNumber !== undefined ? updateParams.taxNumber : companyData?.taxNumber,
            website: updateParams.website !== undefined ? updateParams.website : companyData?.website
        };

        return <View
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            <TextInput
                title={localize("companyOrOrganization")}
                type={!currentData.title || currentData.title.length <= 2 ? "danger" : "default"}
                hintText={!currentData.title || currentData.title.length <= 2 ? (localize("companyTitleTooShort")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.title}
                defaultValue={companyData.title}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        title: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("foundationYear")}
                type={!currentData.foundationYear || currentData.foundationYear <= 1923 || currentData.foundationYear >= 2026 ? "danger" : "default"}
                hintText={!currentData.foundationYear || currentData.foundationYear <= 1923 || currentData.foundationYear >= 2026 ? (localize("invalidFoundationYear")) : undefined}
                spreadBehaviour="stretch"
                placeholder={String(companyData.foundationYear)}
                defaultValue={String(companyData.foundationYear)}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        foundationYear: Number(e)
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <SelectBox
                initialSelectedItems={sectorInitialSelected}
                keyExtractor={(item) => item._id }
                titleExtractor={(item) => item.value}
                spreadBehaviour="stretch"
                data={sectorsData}
                title={localize("sector")}
                isWorkWithRealtime={false}
                isCleanEnabled={false}
                minChoice={1}
                isMultipleSelect={true}
                selectSheetProps={{
                    style:{
                        borderTopRightRadius: radiuses.sm,
                        borderTopLeftRadius: radiuses.sm
                    }
                }}
                onOk={(
                    selectedItems
                ) => {
                    if(selectedItems && selectedItems.length) {
                        const sectorIDs = selectedItems.map(e => e.__key);
                        setUpdateParams({
                            ...updateParams,
                            sectorIDs: sectorIDs
                        });
                    }
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("companyEmailAddress")}
                type={!currentData.mail || !validator.isEmail(currentData.mail) ? "danger" : "default"}
                hintText={!currentData.mail || !validator.isEmail(currentData.mail) ? (localize("invalidEmail")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.mail}
                defaultValue={companyData.mail}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        mail: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextAreaInput
                title={localize("aboutCompany")}
                type={!currentData.aboutUs || currentData.aboutUs.length <= 3 ? "danger" : "default"}
                hintText={!currentData.aboutUs || currentData.aboutUs.length <= 3 ? (localize("aboutCannotBeEmpty")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.aboutUs}
                defaultValue={companyData.aboutUs}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        aboutUs: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("address")}
                type={!currentData.address || currentData.address.length <= 2 ? "danger" : "default"}
                hintText={!currentData.address || currentData.address.length <= 2 ? (localize("invalidAddress")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.address}
                defaultValue={companyData.address}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        address: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("companyPhone")}
                type={!currentData.phoneNumber || !validator.isMobilePhone(currentData.phoneNumber) ? "danger" : "default"}
                hintText={!currentData.phoneNumber || !validator.isMobilePhone(currentData.phoneNumber) ? (localize("invalidPhoneNumber")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.phoneNumber}
                defaultValue={companyData.phoneNumber}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        phoneNumber: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("taxOffice")}
                type={!currentData.taxOffice || currentData.taxOffice.length <= 2 ? "danger" : "default"}
                hintText={!currentData.taxOffice || currentData.taxOffice.length <= 2 ? (localize("invalidTaxOffice")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.taxOffice}
                defaultValue={companyData.taxOffice}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        taxOffice: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("taxNumber")}
                type={!currentData.taxNumber || currentData.taxNumber.length <= 9 ? "danger" : "default"}
                hintText={!currentData.taxNumber || currentData.taxNumber.length <= 9 ? (localize("invalidTaxNumber")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.taxNumber}
                defaultValue={companyData.taxNumber}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        taxNumber: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <SelectBox
                initialSelectedItems={companyData.isCompanyExports ? [
                    {
                        __key: "true",
                        __title: "Evet"
                    }
                ] : [
                    {
                        __key: "false",
                        __title: "Hayır"
                    }
                ]}
                keyExtractor={(item) => item.key }
                titleExtractor={(item) => item.value}
                spreadBehaviour="stretch"
                data={IS_EXPORTS}
                title={localize("doesExport")}
                isWorkWithRealtime={false}
                minChoice={1}
                maxChoice={1}
                isMultipleSelect={false}
                selectSheetProps={{
                    style:{
                        borderTopRightRadius: radiuses.sm,
                        borderTopLeftRadius: radiuses.sm
                    }
                }}
                onOk={(
                    selectedItems
                ) => {
                    if(selectedItems && selectedItems.length) {
                        const _isExports = selectedItems[0]?.__key === "true" ? true : false;

                        setUpdateParams({
                            ...updateParams,
                            isExports: _isExports
                        });
                    }
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("website")}
                type={!currentData.website || !validator.isURL(currentData.website, {
                    validate_length: true
                }) ? "danger" : "default"}
                hintText={!currentData.website || !validator.isURL(currentData.website, {
                    validate_length: true
                }) ? (localize("invalidWebsite")) : undefined}
                spreadBehaviour="stretch"
                placeholder={companyData.website}
                defaultValue={companyData.website}
                onChangeText={(e) => {
                    setUpdateParams({
                        ...updateParams,
                        website: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
        </View>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.system.low.success,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Check
                        color={colors.project.brandPrimary}
                        size={30}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            primaryButtonProps={{
                customBorderColor: colors.project.brandPrimary,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.brandPrimary,
                title: localize("backToHome"),
                onPress: () => {
                    setSuccessDialog(false);
                    navigation.navigate("Profile");
                }
            }}
            content={localize("changesUnderReview")}
            onOverlayPress={() => {
                setSuccessDialog(false);
                navigation.navigate("Profile");
            }}
            isVisible={successDialog}
            variant="ok"
        />;
    };

    const renderVerificationDialog = () => {
        return <Dialog
            secondaryButtonProps={{
                customBorderColor: colors.project.black,
                customTextColor: colors.project.black,
                customColor: colors.project.brandWhite,
                title: localize("abandon"),
                onPress: () => {
                    setVerificationDialog(false);
                }
            }}
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.system.low.success,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingLg
                        }
                    ]}
                >
                    <ArrowLeftRight
                        color={colors.project.brandPrimary}
                        size={40}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("confirmUpdateEditRequest")}
                </Text>
            </View>}
            primaryButtonProps={{
                customBorderColor: colors.project.brandPrimary,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.brandPrimary,
                title: localize("update"),
                onPress: () => {
                    updateEditRequest();
                }
            }}
            content={localize("submitForApprovalDescription")}
            onOverlayPress={() => {
                setVerificationDialog(false);
            }}
            isVisible={verificationDialog}
            variant="yes-no"
        />;
    };

    const renderDeleteSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.system.low.warning,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Check
                        color={colors.project.gold}
                        size={30}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("requestCancelled")}
                </Text>
            </View>}
            primaryButtonProps={{
                customBorderColor: colors.project.gold,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.gold,
                title: localize("goBack"),
                onPress: () => {
                    setDeleteSuccessDialog(false);
                    navigation.navigate("Profile");
                }
            }}
            content={localize("editRequestWithdrawn")}
            onOverlayPress={() => {
                setDeleteSuccessDialog(false);
                navigation.goBack();
            }}
            isVisible={deleteSuccessDialog}
            variant="ok"
        />;
    };

    const renderDeleteVerificationDialog = () => {
        return <Dialog
            secondaryButtonProps={{
                customBorderColor: colors.project.black,
                customTextColor: colors.project.black,
                customColor: colors.project.brandWhite,
                title: localize("abandon"),
                onPress: () => {
                    setDeleteVerificationDialog(false);
                }
            }}
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.system.low.warning,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingLg
                        }
                    ]}
                >
                    <PencilOff
                        color={colors.project.gold}
                        size={40}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("confirmCancelEditRequest")}
                </Text>
            </View>}
            primaryButtonProps={{
                title: localize("cancel-it"),
                customBorderColor: colors.project.gold,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.gold,
                onPress: () => {
                    cancelEditRequest();
                }
            }}
            content={localize("cancelEditRequestDescription")}
            onOverlayPress={() => {
                setDeleteVerificationDialog(false);
            }}
            isVisible={deleteVerificationDialog}
            variant="yes-no"
        />;
    };

    const renderActionButtons = () => {
        const currentData = {
            title: updateParams.title !== undefined ? updateParams.title : companyData?.title,
            foundationYear: updateParams.foundationYear !== undefined ? updateParams.foundationYear : companyData?.foundationYear,
            mail: updateParams.mail !== undefined ? updateParams.mail : companyData?.mail,
            aboutUs: updateParams.aboutUs !== undefined ? updateParams.aboutUs : companyData?.aboutUs,
            address: updateParams.address !== undefined ? updateParams.address : companyData?.address,
            phoneNumber: updateParams.phoneNumber !== undefined ? updateParams.phoneNumber : companyData?.phoneNumber,
            taxOffice: updateParams.taxOffice !== undefined ? updateParams.taxOffice : companyData?.taxOffice,
            taxNumber: updateParams.taxNumber !== undefined ? updateParams.taxNumber : companyData?.taxNumber,
            website: updateParams.website !== undefined ? updateParams.website : companyData?.website
        };

        const isFormValid = Boolean(
            currentData.aboutUs && currentData.aboutUs.length > 3 &&
            currentData.mail && validator.isEmail(currentData.mail) &&
            currentData.title && currentData.title.length > 2 &&
            currentData.address && currentData.address.length > 2 &&
            currentData.taxOffice && currentData.taxOffice.length > 2 &&
            currentData.taxNumber && currentData.taxNumber.length > 9 &&
            currentData.website && validator.isURL(currentData.website, {
                validate_length: true
            }) &&
            currentData.foundationYear && currentData.foundationYear > 1923 && currentData.foundationYear < 2026 &&
            currentData.phoneNumber && validator.isMobilePhone(currentData.phoneNumber)
        );

        return <View
            style={{
                marginBottom: spaces.spacingMd
            }}
        >
            <Button
                size="large"
                title={localize("updateEditRequest")}
                type="neutral"
                customColor="black"
                customBorderColor="black"
                customTextColor={colors.project.brandWhite}
                onPress={() => {
                    if(!isFormValid) {
                        NCoreUIKitToast.open({
                            title: localize("error"),
                            subTitle: localize("pleaseFillAllFields"),
                            type: "danger"
                        });
                        return;
                    }
                    setVerificationDialog(true);
                }}
                isDisabled={!isFormValid || (Object.keys(updateParams).length === 1 && !selctedFile)}
                spreadBehaviour="stretch"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <Button
                size="large"
                title={localize("cancelEditRequest")}
                customColor="gold"
                customBorderColor="gold"
                customTextColor="black"
                onPress={() => {
                    setDeleteVerificationDialog(true);
                }}
                spreadBehaviour="stretch"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
        </View>;
    };

    if(loading) {
        return <PageContainer>
            <ActivityIndicator
                size={"large"}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderProfilePhoto()}
        {renderMyCompanyData()}
        {renderActionButtons()}
        {renderSuccessDialog()}
        {renderVerificationDialog()}
        {renderDeleteVerificationDialog()}
        {renderDeleteSuccessDialog()}
    </PageContainer>;
};
export default CompanyEditView;
